import React, { useCallback, useState, useMemo } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AsyncSingletonError } from '@proscom/prostore-react';
import {
  LandingGrid,
  LandingLayoutContent
} from '../../../common/components/Layout/Layout';
import { Title, TitleVariant } from '../../../common/components/ui/Title/Title';
import { TextLink } from '../../../common/components/ui/TextLink/TextLink';
import { Modal } from '../../../common/components/ui/Modal/Modal';
import { DocumentSection } from '../../../common/components/ui/DocumentSection/DocumentSection';
import { DocumentSectionType } from '../../../graphql/types';
import { Document } from '../../../common/components/ui/DocumentSection/Document/Document';
import {
  Button,
  ButtonSize,
  ButtonVariant
} from '../../../common/components/ui/Button';
import { ReactComponent as AddIcon } from '../../../assets/img/icons/plus.svg';
import { useGraphqlMutation } from '../../../common/hooks/utils/useGraphqlMutation';
import { tryAppendErrorId } from '../../../graphql/graphqlErrors';
import { DocumentsOnWelcomePage } from '../../../store/documents/DocumentsOnWelcomePage';
import { useFileDownload } from '../../../common/hooks/useFileDownload';
import { FileTypeEnum } from '../../../store/FileTypeEnum';
import s from './Documents.module.scss';

type DocumentItemProps = {
  title: string;
} & (
  | {
      fileId: string;
      link?: never;
    }
  | {
      link: string;
      fileId?: never;
    }
);

interface DocumentsSectionProps {
  id: string;
  title: string;
  items?: DocumentItemProps[];
  allItems?: DocumentItemProps[][];
  onClick?: () => void;
}

interface DocumentsModalContentProps
  extends Pick<DocumentsSectionProps, 'allItems'> {}

const DocumentsModalContent: React.FC<DocumentsModalContentProps> = ({
  allItems
}) => {
  const { handle: handleOpenDocument } = useFileDownload(
    undefined,
    FileTypeEnum.document
  );

  return (
    <div className={s.DocumentsModal__grid}>
      {allItems?.map((chunk, iChunk) => (
        <div key={iChunk}>
          {chunk.map((item, iItem) => (
            <div key={iItem} className={s.DocumentsModal__item}>
              <TextLink
                key={iItem}
                href={item.fileId ? undefined : item.link}
                onClick={item.fileId ? handleOpenDocument(item.fileId) : undefined}
              >
                {item.title}
              </TextLink>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const DocumentsSection: React.FC<DocumentsSectionProps> = ({
  title,
  items,
  allItems
}) => {
  const [modalActive, setModalActive] = useState(false);
  const hasList = items && items.length > 0;

  const handleItemClick = useCallback((e) => {
    e.preventDefault();
    setModalActive(true);
  }, []);

  const { handle: handleOpenDocument } = useFileDownload(
    undefined,
    FileTypeEnum.document
  );

  return (
    <>
      <Link
        className={clsx(s.DocumentsSection, {
          [s.DocumentsSection_clickable]: allItems && allItems.length > 0
        })}
        to={''}
        onClick={handleItemClick}
      >
        <div
          className={clsx(s.DocumentsSection__head, {
            [s.DocumentsSection__head_noList]: !hasList
          })}
        >
          <Title title={title} variant={TitleVariant.h3} />
        </div>

        {hasList && (
          <div className={s.DocumentsSection__content}>
            {hasList && (
              <div className={s.DocumentsSection__list}>
                {items?.map((item) => (
                  <div
                    className={s.DocumentsSection__item}
                    onClick={() => handleOpenDocument(item.fileId)}
                  >
                    {item.title}
                  </div>
                ))}
              </div>
            )}
            <div className={s.DocumentsSection__linkBox}>
              <TextLink
                iconProps={{
                  boxSize: 12
                }}
                isClickable={false}
                noHover
                showArrow
              >
                Подробнее
              </TextLink>
            </div>
          </div>
        )}
      </Link>

      {hasList && (
        <Modal
          title={title}
          isOpen={modalActive}
          onClose={() => setModalActive(false)}
          size={
            allItems?.length
              ? {
                  maxWidth: allItems.length === 1 ? 668 : 960
                }
              : undefined
          }
        >
          <DocumentsModalContent allItems={allItems} />
        </Modal>
      )}
    </>
  );
};

export interface DocumentsSectionsListProps {
  documentSections: DocumentSectionType[];
  editable?: boolean;
}

export const DocumentsSectionsList: React.FC<DocumentsSectionsListProps> = ({
  documentSections,
  editable
}) => {
  const [modalActive, setModalActive] = useState(false);
  const [selSectionId, setSelSectionId] = useState<string | null>(null);

  const selectedSection = useMemo(() => {
    return documentSections.find((s) => s.id === selSectionId);
  }, [documentSections, selSectionId]);

  const createDocumentSection = useGraphqlMutation(
    DocumentsOnWelcomePage.CreateDocumentSection
  );

  const createDocumentSectionRun = createDocumentSection.run;

  const handleAddNewSection = () => {
    createDocumentSectionRun({
      variables: {
        input: {
          name: 'Новый раздел'
        }
      }
    })
      .then((result) => {
        if (!result.data?.result) {
          throw new Error('UnexpectedResult');
        }
        toast.success('Раздел добавлен');
      })
      .catch((err) => {
        if (err instanceof AsyncSingletonError) return;
        tryAppendErrorId(
          err,
          'Произошла ошибка при создании раздела. Попробуйте снова'
        );
      });
  };

  if (!documentSections.length && !editable) {
    return null;
  }

  return (
    <LandingLayoutContent className={s.Documents}>
      <Title className={s.Documents__title} variant={TitleVariant.h2}>
        Трудоустройство в МФТИ
      </Title>

      <div>
        <TextLink
          className={s.Documents__videoLink}
          href={'https://rutube.ru/video/2a6c7def475e5c79c8a847b58cc6fe84/'}
          noUnderline
        >
          Видеоролик &laquo;Трудоустройство в МФТИ&raquo;
        </TextLink>
      </div>
      {editable && (
        <div className={s.Documents__addSection}>
          <Button
            variant={ButtonVariant.secondary}
            size={ButtonSize.small}
            iconLeft={<AddIcon />}
            classes={{
              root: s.DocumentSectionTitle__addButton
            }}
            onClick={handleAddNewSection}
            loading={createDocumentSection.loading}
          >
            Добавить раздел
          </Button>
        </div>
      )}
      {editable ? (
        documentSections.map((section) => (
          <DocumentSection
            key={section.id}
            data={section}
            editable={editable}
            storeService={DocumentsOnWelcomePage}
            withExternalLink
          />
        ))
      ) : (
        <LandingGrid className={s.Documents__grid}>
          {documentSections
            .filter((item) => (editable ? true : item.documents?.length))
            .map((item, iItem) => {
              const documents = (
                item.documents?.sort(
                  (a, b) => (a?.priority || 0) - (b?.priority || 0)
                ) || []
              ).map((doc) => ({
                title: doc.name,
                ...(doc.file_id
                  ? { fileId: doc.file_id }
                  : { link: doc.external_link || '' })
              }));

              return (
                <DocumentsSection
                  key={iItem}
                  id={item.id}
                  title={item.name}
                  items={documents.slice(0, 6)}
                  allItems={[documents]}
                />
              );
            })}
        </LandingGrid>
      )}

      {!editable && (
        <Modal
          className={s.SectionsList__docsModal}
          title={selectedSection?.name}
          isOpen={modalActive}
          onClose={() => setModalActive(false)}
          onClosed={() => setSelSectionId(null)}
        >
          {selectedSection?.documents?.map((doc) => (
            <Document key={doc.id} {...doc} />
          ))}
        </Modal>
      )}
    </LandingLayoutContent>
  );
};
